import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { commentApi } from '../../services/api';
import { CommentDto, CommentListResponse, CommentStatsDto } from '../../types/Comment';

const CommentsPage: React.FC = () => {
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [stats, setStats] = useState<CommentStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [searchUserId, setSearchUserId] = useState('');
  const [searchUserName, setSearchUserName] = useState('');
  const [searchContent, setSearchContent] = useState('');
  const [searchFeatureId, setSearchFeatureId] = useState('');
  const [searchFeatureName, setSearchFeatureName] = useState('');
  const [searchFeatureType, setSearchFeatureType] = useState('');
  const [selectedComments, setSelectedComments] = useState<Set<string>>(new Set());
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'deleted'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const includeDeleted = statusFilter === 'deleted' || statusFilter === 'all';
      const response: CommentListResponse = await commentApi.getComments({
        page,
        pageSize,
        includeDeleted,
        userId: searchUserId || undefined,
        featureId: searchFeatureId || undefined,
        featureName: searchFeatureName || undefined,
        featureType: searchFeatureType || undefined,
      });
      
      let filteredComments = response.comments;
      
      // Apply additional filters
      if (searchUserName) {
        filteredComments = filteredComments.filter(comment => 
          comment.userName?.toLowerCase().includes(searchUserName.toLowerCase())
        );
      }
      
      if (searchContent) {
        filteredComments = filteredComments.filter(comment => 
          comment.content.toLowerCase().includes(searchContent.toLowerCase())
        );
      }
      
      if (statusFilter === 'active') {
        filteredComments = filteredComments.filter(comment => !comment.isDeleted);
      } else if (statusFilter === 'deleted') {
        filteredComments = filteredComments.filter(comment => comment.isDeleted);
      }
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        filteredComments = filteredComments.filter(comment => 
          new Date(comment.clientCreatedAt) >= fromDate
        );
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo + 'T23:59:59');
        filteredComments = filteredComments.filter(comment => 
          new Date(comment.clientCreatedAt) <= toDate
        );
      }
      
      setComments(filteredComments);
      setTotalPages(Math.ceil(filteredComments.length / pageSize));
    } catch (err) {
      console.error('Failed to load comments', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, searchUserId, searchUserName, searchContent, searchFeatureId, searchFeatureName, searchFeatureType, dateFrom, dateTo]);

  useEffect(() => {
    loadComments();
    loadStats();
  }, [page, loadComments]);

  const loadStats = async () => {
    try {
      const statsData = await commentApi.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadComments();
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await commentApi.deleteComment(commentId);
      await loadComments();
      await loadStats();
    } catch (err) {
      alert('Failed to delete comment');
      console.error(err);
    }
  };

  const handleRestore = async (commentId: string) => {
    try {
      await commentApi.restoreComment(commentId);
      await loadComments();
      await loadStats();
    } catch (err) {
      alert('Failed to restore comment');
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedComments.size === 0) {
      alert('No comments selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedComments.size} comments?`)) return;

    try {
      const commentIds = Array.from(selectedComments);
      for (const commentId of commentIds) {
        await commentApi.deleteComment(commentId);
      }
      setSelectedComments(new Set());
      await loadComments();
      await loadStats();
    } catch (err) {
      alert('Failed to delete comments');
      console.error(err);
    }
  };

  const handleDeleteByUser = async (userId: string) => {
    if (!window.confirm(`Are you sure you want to delete all comments from user ${userId}?`)) return;

    try {
      const result = await commentApi.bulkDeleteByUser(userId);
      alert(result.message);
      await loadComments();
      await loadStats();
    } catch (err) {
      alert('Failed to delete user comments');
      console.error(err);
    }
  };

  const handleEdit = (comment: CommentDto) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async () => {
    if (!editingComment) return;

    try {
      await commentApi.updateComment(editingComment, { content: editContent });
      setEditingComment(null);
      setEditContent('');
      await loadComments();
    } catch (err) {
      alert('Failed to update comment');
      console.error(err);
    }
  };

  const toggleCommentSelection = (commentId: string) => {
    const newSelection = new Set(selectedComments);
    if (newSelection.has(commentId)) {
      newSelection.delete(commentId);
    } else {
      newSelection.add(commentId);
    }
    setSelectedComments(newSelection);
  };
  
  const selectAllComments = () => {
    setSelectedComments(new Set(comments.map(c => c.id)));
  };
  
  const deselectAllComments = () => {
    setSelectedComments(new Set());
  };
  
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };
  
  const getFeatureTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      '1': 'KM Marker',
      '2': 'DOC Hut',
      '3': 'DOC Campsite',
      '4': 'YHA Hostel',
      '5': 'Private Campsite',
      '6': 'Water Source',
      '7': 'Partner',
      '8': 'POI',
      '9': 'Alert',
      '10': 'Track Section',
      '11': 'Bypass Section'
    };
    return types[type] || `Type ${type}`;
  };
  
  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };
  
  const clearAllFilters = () => {
    setSearchUserId('');
    setSearchUserName('');
    setSearchContent('');
    setSearchFeatureId('');
    setSearchFeatureName('');
    setSearchFeatureType('');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  if (loading && comments.length === 0) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            ← Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Comment Management</h1>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-gray-900">{stats.totalComments}</div>
              <div className="text-sm text-gray-500">Total Comments</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{stats.activeComments}</div>
              <div className="text-sm text-gray-500">Active Comments</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{stats.deletedComments}</div>
              <div className="text-sm text-gray-500">Deleted Comments</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{stats.totalUsers}</div>
              <div className="text-sm text-gray-500">Unique Users</div>
            </div>
          </div>
        )}

        {/* Enhanced Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters & Search</h3>
            <button
              onClick={clearAllFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>
          
          {/* Search Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <svg className="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search in comment content..."
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
                className="pl-10 px-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <input
              type="text"
              placeholder="Search by username"
              value={searchUserName}
              onChange={(e) => setSearchUserName(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              placeholder="User ID"
              value={searchUserId}
              onChange={(e) => setSearchUserId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          {/* Filter Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div className="relative">
              <svg className="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by item name..."
                value={searchFeatureName}
                onChange={(e) => setSearchFeatureName(e.target.value)}
                className="pl-10 px-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <select
              value={searchFeatureType}
              onChange={(e) => setSearchFeatureType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Feature Types</option>
              <option value="1">KM Marker</option>
              <option value="2">DOC Hut</option>
              <option value="3">DOC Campsite</option>
              <option value="4">YHA Hostel</option>
              <option value="5">Private Campsite</option>
              <option value="6">Water Source</option>
              <option value="7">Partner</option>
              <option value="8">POI</option>
              <option value="9">Alert</option>
              <option value="10">Track Section</option>
              <option value="11">Bypass Section</option>
            </select>
            <input
              type="text"
              placeholder="Feature ID (advanced)"
              value={searchFeatureId}
              onChange={(e) => setSearchFeatureId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              title="Search by database feature ID"
            />
            <input
              type="date"
              placeholder="From date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="date"
              placeholder="To date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          {/* Status Filter & Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-4 py-2 text-sm font-medium ${statusFilter === 'all' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  All
                </button>
                <button
                  onClick={() => setStatusFilter('active')}
                  className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${statusFilter === 'active' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Active
                </button>
                <button
                  onClick={() => setStatusFilter('deleted')}
                  className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${statusFilter === 'deleted' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Deleted
                </button>
              </div>
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Enhanced Bulk Actions */}
        {selectedComments.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-xl mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  <span className="font-medium text-indigo-900">{selectedComments.size} comments selected</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <button
                    onClick={selectAllComments}
                    className="text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={deselectAllComments}
                    className="text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Comments List */}
        <div className="space-y-4">
          {comments.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No comments found</h3>
              <p className="mt-1 text-sm text-gray-500">Try adjusting your filters to see more comments.</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div
                key={comment.id}
                className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-md ${
                  comment.isDeleted 
                    ? 'border-red-200 bg-red-50' 
                    : selectedComments.has(comment.id)
                    ? 'border-indigo-300 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={selectedComments.has(comment.id)}
                        onChange={() => toggleCommentSelection(comment.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </div>
                    
                    {/* User Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">
                          {getUserInitials(comment.userName || comment.userId)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Status Badge & User Info */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900 truncate">
                            {comment.userName || 'Anonymous User'}
                          </h4>
                          {comment.isDeleted ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Deleted
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                          {comment.likes > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              👍 {comment.likes}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDistanceToNow(new Date(comment.clientCreatedAt), { addSuffix: true })}
                        </div>
                      </div>
                      
                      {/* Comment Content */}
                      {editingComment === comment.id ? (
                        <div className="mb-4">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            rows={4}
                          />
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={handleSaveEdit}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingComment(null);
                                setEditContent('');
                              }}
                              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4">
                          <p className="text-gray-900 leading-relaxed">
                            {comment.content.length > 300 
                              ? truncateText(comment.content, 300)
                              : comment.content
                            }
                          </p>
                        </div>
                      )}
                      
                      {/* Metadata */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          <span>{getFeatureTypeLabel(comment.featureType || '')}</span>
                        </div>
                        {comment.featureName && (
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-medium text-gray-700">{comment.featureName}</span>
                          </div>
                        )}
                        {comment.featureId && (
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                            </svg>
                            <span className="text-xs text-gray-400">ID: {comment.featureId}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{format(new Date(comment.clientCreatedAt), 'MMM d, yyyy h:mm a')}</span>
                        </div>
                      </div>
                      
                      {/* Deletion Info */}
                      {comment.isDeleted && comment.deletedBy && comment.deletedAt && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <div className="flex items-center gap-2 text-sm text-red-800">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.863-.833-2.633 0L4.188 14.5C3.417 16.333 4.38 18 5.92 18z" />
                            </svg>
                            <span>
                              Deleted by <strong>{comment.deletedBy}</strong> on{' '}
                              {format(new Date(comment.deletedAt), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Menu */}
                    <div className="flex-shrink-0">
                      <div className="flex flex-col sm:flex-row gap-1">
                        {!comment.isDeleted && (
                          <>
                            <button
                              onClick={() => handleEdit(comment)}
                              className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Edit comment"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(comment.id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete comment"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}
                        {comment.isDeleted && (
                          <button
                            onClick={() => handleRestore(comment.id)}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                            title="Restore comment"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteByUser(comment.userId)}
                          className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Delete all comments by this user"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Previous</span>
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        pageNum === page
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-2"
              >
                <span className="hidden sm:inline">Next</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </nav>
            <div className="ml-4 flex items-center text-sm text-gray-500">
              Page {page} of {totalPages}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsPage;